import React, { useState, useEffect } from 'react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { FaCalendar, FaUser, FaStethoscope, FaDownload, FaFilter, FaProcedures } from 'react-icons/fa';
import { SchedulingProvider, BookedProcedureAppointment } from '../../types';

const BookedProcedures: React.FC = () => {
    const { token } = useAuth();
    const [appointments, setAppointments] = useState<BookedProcedureAppointment[]>([]);
    const [providers, setProviders] = useState<SchedulingProvider[]>([]);
    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        loadProviders();
        // Set default date range to current month
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartDate(firstDay.toISOString().split('T')[0]);
        setEndDate(lastDay.toISOString().split('T')[0]);
    }, []);

    useEffect(() => {
        if (selectedProvider && startDate && endDate) {
            loadAppointments();
        }
    }, [selectedProvider, startDate, endDate]);

    const loadProviders = async () => {
        if (!token) return;
        try {
            const response = await apiCall('/providers/providers/', {
                method: 'GET'
            }, token);
            const data = await response.json();
            const providerList = data.results || data;
            
            // Filter to only doctors and specialists who might do procedures
            const surgeons = providerList.filter((p: SchedulingProvider) => 
                p.provider_type === 'doctor' || p.provider_type === 'specialist'
            );
            setProviders(surgeons);
            
            // Select first provider by default
            if (surgeons.length > 0 && !selectedProvider) {
                setSelectedProvider(surgeons[0].id);
            }
        } catch (error) {
            console.error('Error loading providers:', error);
        }
    };

    const loadAppointments = async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                provider: selectedProvider,
                start_date: startDate,
                end_date: endDate
            });

            const response = await apiCall(`/scheduling/appointments/?${params}`, {
                method: 'GET'
            }, token || undefined);
            const data = await response.json();
            const appointmentList = data.results || data;
            
            // Filter to only show appointments with procedure descriptions or procedure types
            // AND only from Hospital facility types (not Consulting Rooms)
            const procedures = appointmentList.filter((apt: BookedProcedureAppointment) => {
                const hasProcedure = apt.procedure_description || apt.procedure_type_name;
                const isHospital = apt.facility_type_name?.toLowerCase() === 'hospital';
                return hasProcedure && isHospital;
            });
            
            setAppointments(procedures);
        } catch (error) {
            console.error('Error loading appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        // Create CSV content
        const headers = ['Date', 'Name', 'Patient Name', 'Patient Phone', 'Location Name', 'Fee', 'Deposits', 'Checklist Alerts', 'Anaesthetist'];
        const rows = appointments.map(apt => [
            new Date(apt.start_time).toLocaleDateString(),
            apt.procedure_type_name || apt.procedure_description || 'N/A',
            apt.patient_name || 'N/A',
            'N/A', // Patient phone - would need to be added to serializer
            apt.room_name || 'N/A',
            'N/A', // Fee - would need to be calculated
            'N/A', // Deposits - would need to be tracked
            'N/A', // Checklist alerts - would need to be implemented
            apt.anaesthetist_name || 'N/A'
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `booked-procedures-${selectedProvider}-${startDate}-${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const selectedProviderName = providers.find(p => p.id === selectedProvider)?.full_name || '';

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                    <FaProcedures className="text-primary" />
                    Booked Procedures
                </h1>
                <button
                    onClick={handleExport}
                    className="btn btn-outline btn-primary"
                    disabled={appointments.length === 0}
                >
                    <FaDownload className="mr-2" />
                    Download Excel
                </button>
            </div>

            {/* Filter Section */}
            <div className="bg-base-200 p-4 rounded-lg mb-6">
                <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium whitespace-nowrap">Doctor *</span>
                        <select
                            value={selectedProvider}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedProvider(e.target.value)}
                            className="select select-bordered"
                        >
                            <option value="">Select a doctor</option>
                            {providers.map(provider => (
                                <option key={provider.id} value={provider.id}>
                                    {provider.first_name} {provider.last_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium whitespace-nowrap">Start Date</span>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartDate(e.target.value)}
                            className="input input-bordered"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium whitespace-nowrap">End Date</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndDate(e.target.value)}
                            className="input input-bordered"
                        />
                    </div>

                    {selectedProvider && (
                        <button
                            onClick={loadAppointments}
                            className="btn btn-primary"
                        >
                            <FaFilter className="mr-2" />
                            Show Procedures
                        </button>
                    )}
                </div>
            </div>

            {/* Results Section */}
            {loading ? (
                <div className="text-center py-8">
                    <div className="loading loading-spinner loading-lg"></div>
                    <p className="mt-4 text-slate-600">Loading procedures...</p>
                </div>
            ) : selectedProvider ? (
                <div>
                    <div className="mb-4 text-base-content opacity-70">
                        Showing {appointments.length} procedure(s) for <strong>{selectedProviderName}</strong>
                        {' '}from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Name</th>
                                    <th>Patient Name</th>
                                    <th>Patient Phone</th>
                                    <th>Location Name</th>
                                    <th>Fee</th>
                                    <th>Deposits</th>
                                    <th>Checklist Alerts</th>
                                    <th>Anaesthetist</th>
                                    <th>Commands</th>
                                </tr>
                            </thead>
                            <tbody>
                                {appointments.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="text-center py-8 text-base-content opacity-60">
                                            {selectedProvider 
                                                ? 'No procedures found for the selected period' 
                                                : 'Please select a doctor to view procedures'}
                                        </td>
                                    </tr>
                                ) : (
                                    appointments.map(appointment => (
                                        <tr key={appointment.id}>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <FaCalendar className="text-slate-400" />
                                                    {new Date(appointment.start_time).toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="font-medium">
                                                    {appointment.procedure_type_name || appointment.procedure_description}
                                                </div>
                                                {appointment.procedure_type_name && appointment.procedure_description && (
                                                    <div className="text-sm text-slate-500">{appointment.procedure_description}</div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <FaUser className="text-slate-400" />
                                                    {appointment.patient_name || 'N/A'}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-slate-500">N/A</span>
                                            </td>
                                            <td>{appointment.room_name || 'N/A'}</td>
                                            <td>
                                                <span className="text-slate-500">N/A</span>
                                            </td>
                                            <td>
                                                <span className="text-slate-500">$0.00</span>
                                            </td>
                                            <td>
                                                <span className="text-slate-500">-</span>
                                            </td>
                                            <td>
                                                {appointment.anaesthetist_name ? (
                                                    <div className="flex items-center gap-2 text-warning">
                                                        <FaStethoscope />
                                                        {appointment.anaesthetist_name}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400">N/A</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="dropdown dropdown-end">
                                                    <button tabIndex={0} className="btn btn-ghost btn-sm">
                                                        ⋮
                                                    </button>
                                                    <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52">
                                                        <li><a>View</a></li>
                                                        <li><a>Edit</a></li>
                                                        <li><a className="text-error">Cancel</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="text-center py-12 text-slate-500">
                    <FaFilter className="text-6xl mx-auto mb-4 text-slate-300" />
                    <p className="text-lg">Please select a doctor to view their booked procedures</p>
                </div>
            )}
        </div>
    );
};

export default BookedProcedures;
